import { Combobox } from "@headlessui/react"
import algoliasearch from "algoliasearch"
import React, { useEffect, useRef, useState } from "react"
import { Configure, InstantSearch, useHits, useSearchBox } from "react-instantsearch"

import { useContainerDimensions } from "../../hooks/useContainerDimensions"
import { sanitize } from "../../utils/utils"

import {
  BASE_INPUT_CLASSNAMES,
  BASE_LABEL_CLASSNAMES,
  DROPDOWN_CLASSNAMES,
  DROPDOWN_OPTION_CLASSNAMES,
  SearchMagnifyingGlass
} from "./Inputs"
import Typography from "./Typography"

const searchClient = algoliasearch(
  process.env.ALGOLIA_HEALME_SEARCH_APPLICATION_ID,
  process.env.ALGOLIA_HEALME_SEARCH_CLIENT_API_KEY
)

export const AlgoliaSearchBox = ({ indexName, explanatorySubtext, ...rest }) => (
  <InstantSearch indexName={indexName} searchClient={searchClient}>
    <Search {...rest} />
    {explanatorySubtext && <Typography variant="micro">{explanatorySubtext}</Typography>}
  </InstantSearch>
)

export const AlgoliaSearchProvider = ({ indexName, children }) => (
  <InstantSearch indexName={indexName} searchClient={searchClient}>
    {children}
  </InstantSearch>
)

const Search = ({ onChange, id, label, hitsPerPage = 10, filters, initialValue, fallbackOption, ...rest }) => {
  id ||= "search-" + label.toLowerCase().replace(" ", "-")
  const { query, refine } = useSearchBox()
  const { hits } = useHits()
  const [inputValue, setInputValue] = useState(initialValue ?? query)
  const ref = useRef()
  const { width } = useContainerDimensions(ref)
  const hasInitializedWithValue = useRef(false)

  // When we have an initial value (e.g. existing issuer/credential on pro credential page),
  // run the Algolia search so the current selection appears in results instead of "No results found".
  useEffect(() => {
    if (initialValue && !hasInitializedWithValue.current) {
      hasInitializedWithValue.current = true
      setInputValue(initialValue)
      refine(initialValue)
    }
  }, [initialValue, refine])

  const setQuery = (newQuery) => {
    setInputValue(newQuery)
    refine(newQuery)
  }

  // Show current selection even when it's not in Algolia (e.g. rejected issuer, or not indexed yet).
  const fallbackIncluded =
    fallbackOption &&
    !hits.some(
      (h) =>
        String(h.objectID) === String(fallbackOption.objectID) ||
        (h.name && fallbackOption.name && h.name.trim() === fallbackOption.name.trim())
    )
  const options = fallbackIncluded ? [fallbackOption, ...hits] : hits

  return (
    <>
      <Configure hitsPerPage={hitsPerPage} filters={filters} />
      <Combobox
        value={inputValue}
        onChange={(value) => {
          setInputValue(value)
          onChange?.(value)
        }}>
        {label && (
          <Combobox.Label htmlFor={id} className={BASE_LABEL_CLASSNAMES}>
            {label}
          </Combobox.Label>
        )}
        <div className="relative">
          <Combobox.Input
            id={id}
            ref={ref}
            {...rest}
            onChange={(event) => setQuery(event.target.value)}
            displayValue={(hit) => (hit && typeof hit === "object" && hit.name) || inputValue || initialValue || ""}
            className={BASE_INPUT_CLASSNAMES}
          />
          <SearchMagnifyingGlass />
        </div>
        <Combobox.Options className={"fixed " + DROPDOWN_CLASSNAMES} style={{ maxWidth: width }}>
          {options.map((hit) => (
            <Combobox.Option key={hit.objectID} value={hit} className={DROPDOWN_OPTION_CLASSNAMES}>
              <div dangerouslySetInnerHTML={{ __html: sanitize(hit.name) }} />
            </Combobox.Option>
          ))}
          {options.length === 0 && <div className="px-3">No results found</div>}
        </Combobox.Options>
      </Combobox>
    </>
  )
}
